/**
 * Запросы финансового блока — в одном месте.
 *
 * Разделы спрашивают одно и то же одинаковыми телами, чтобы кеш срабатывал:
 * обзор и раздел «Дебиторка» делят один ответ, P&L и обзор — другой.
 */

import { useFinance } from './context'
import { useReport, type FinanceReport, type ReportState } from './data'
import type { DateRange } from './period'
import { useCoverage, type Coverage } from './coverage'

export const SALES_METRICS = ['revenue', 'revenue_with_cost', 'cost', 'gross_profit', 'margin_percent', 'revenue_without_cost']
export const DEBT_METRICS = ['amount', 'not_overdue', 'overdue_1_30', 'overdue_31_60', 'overdue_61_90', 'overdue_90_plus', 'max_overdue_days']

/** Откуда брать продажи: с маржой, если она выдана, иначе только выручку. */
export function useSalesSource(): { code: FinanceReport; withMargin: boolean; metrics: string[] } | null {
  const { catalog } = useFinance()
  // Показатель может быть скрыт администратором: запрос скрытого — отказ 403,
  // поэтому просим только выданное. Без себестоимости — та же ветка «только
  // выручка», что и у отчёта без маржи.
  const granted = (code: FinanceReport) => new Set(catalog.descriptor(code)?.metrics.map((item) => item.key) ?? [])
  if (catalog.has('margin_by_department')) {
    const keys = granted('margin_by_department')
    if (SALES_METRICS.every((key) => keys.has(key))) return { code: 'margin_by_department', withMargin: true, metrics: SALES_METRICS }
    if (keys.has('revenue')) return { code: 'margin_by_department', withMargin: false, metrics: ['revenue'] }
  }
  if (catalog.has('sales_by_group') && granted('sales_by_group').has('revenue')) {
    return { code: 'sales_by_group', withMargin: false, metrics: ['revenue'] }
  }
  return null
}

export function useSales(dimension: string, range: DateRange, extra?: Record<string, string[]>): ReportState {
  const { scoped, catalog } = useFinance()
  const source = useSalesSource()
  const supported = source ? catalog.hasDimension(source.code, dimension) : false
  return useReport(
    source?.code ?? 'margin_by_department',
    source && supported
      ? scoped(source.code, { period: range, group_by: [dimension], metrics: source.metrics, filters: extra })
      : null,
  )
}

export function useCashByItem(range: DateRange): ReportState {
  const { scoped } = useFinance()
  return useReport('cashflow', scoped('cashflow', { period: range, group_by: ['cashflow_item'], metrics: ['inflow', 'outflow', 'net_flow'] }))
}

export function useCashTrend(range: DateRange): ReportState {
  const { scoped } = useFinance()
  return useReport('cashflow', scoped('cashflow', { period: range, group_by: ['period'], metrics: ['inflow', 'outflow', 'net_flow'] }))
}

/** Дебиторская по контрагентам на последнюю дату. Без лимита: из неё считается и итог, и число должников. */
export function useDebtors(): ReportState {
  const { scoped, receivableFilter } = useFinance()
  return useReport(
    'receivables',
    scoped('receivables', {
      group_by: ['counterparty'],
      metrics: DEBT_METRICS,
      filters: receivableFilter,
      order_by: { key: 'amount', direction: 'desc' },
    }),
  )
}

/** Фильтр «только внешние», если клиент исключил долги между своими. */
function relationFilter(externalOnly: boolean | undefined): Record<string, string[]> {
  return externalOnly ? { relation: ['Внешние'] } : {}
}

/** Обе стороны долга, по одной строке на сторону. Только если бэкенд их различает. */
export function useDebtSides(): ReportState {
  const { scoped, catalog, debtsExternalOnly } = useFinance()
  const filters = relationFilter(debtsExternalOnly)
  return useReport(
    'receivables',
    catalog.hasDimension('receivables', 'kind')
      ? scoped('receivables', {
          group_by: ['kind'],
          metrics: DEBT_METRICS.filter((key) => key !== 'max_overdue_days'),
          ...(Object.keys(filters).length > 0 ? { filters } : {}),
        })
      : null,
  )
}

/**
 * Кредиторская по контрагентам. relation: undefined — по настройке клиента,
 * null — все, строка — «Внешние» или «Между своими».
 */
export function useCreditors(relation?: string | null): ReportState {
  const { scoped, catalog, debtsExternalOnly } = useFinance()
  const filters = relation === undefined ? relationFilter(debtsExternalOnly) : relation ? { relation: [relation] } : {}
  return useReport(
    'receivables',
    catalog.hasDimension('receivables', 'kind')
      ? scoped('receivables', {
          group_by: ['counterparty'],
          metrics: DEBT_METRICS,
          filters: { kind: ['Кредиторская'], ...filters },
          order_by: { key: 'amount', direction: 'desc' },
        })
      : null,
  )
}

/** Дебиторская по контрагентам с явным выбором «внешние / между своими / все». */
export function useDebtorsBy(relation: string | null): ReportState {
  const { scoped, catalog } = useFinance()
  const base: Record<string, string[]> = catalog.hasDimension('receivables', 'kind') ? { kind: ['Дебиторская'] } : {}
  const filters: Record<string, string[]> = { ...base, ...(relation ? { relation: [relation] } : {}) }
  return useReport(
    'receivables',
    scoped('receivables', {
      group_by: ['counterparty'],
      metrics: DEBT_METRICS,
      ...(Object.keys(filters).length > 0 ? { filters } : {}),
      order_by: { key: 'amount', direction: 'desc' },
    }),
  )
}

/**
 * Долги по сторонам и признаку «внешние / между своими» — без фильтра:
 * из него строка «между своими: N ₽ (не включено)».
 */
export function useDebtRelations(): ReportState {
  const { scoped, catalog } = useFinance()
  return useReport(
    'receivables',
    catalog.hasDimension('receivables', 'relation') && catalog.hasDimension('receivables', 'kind')
      ? scoped('receivables', { group_by: ['kind', 'relation'], metrics: ['amount', 'overdue_61_90', 'overdue_90_plus'] })
      : null,
  )
}

/** Откуда филиал у дебиторки: по документу, выведено, не определён. */
export function useDebtBranches(relation: string | null): ReportState {
  const { scoped, catalog, branch } = useFinance()
  const base: Record<string, string[]> = catalog.hasDimension('receivables', 'kind') ? { kind: ['Дебиторская'] } : {}
  const filters: Record<string, string[]> = { ...base, ...(relation ? { relation: [relation] } : {}) }
  return useReport(
    'receivables',
    catalog.hasDimension('receivables', 'branch_source') && !branch
      ? scoped('receivables', {
          group_by: ['branch', 'branch_source'],
          metrics: ['amount'],
          ...(Object.keys(filters).length > 0 ? { filters } : {}),
        })
      : null,
  )
}

// --- Черновой P&L, ФОТ, разделы ДДС ------------------------------------------

export const PNL_METRICS = ['revenue', 'cost', 'gross_profit', 'revenue_without_cost', 'opex', 'ebitda', 'ebitda_margin', 'expense_excluded']

/** Показатели чернового P&L, которые выданы человеку: скрытый в запросе — отказ 403. */
function pnlMetrics(catalog: ReturnType<typeof useFinance>['catalog'], wanted: string[]): string[] {
  const granted = new Set(catalog.descriptor('pnl_management')?.metrics.map((item) => item.key) ?? [])
  return wanted.filter((key) => granted.has(key))
}

export function usePnlByLine(range: DateRange): ReportState {
  const { scoped, catalog } = useFinance()
  const metrics = pnlMetrics(catalog, PNL_METRICS)
  return useReport('pnl_management', metrics.includes('opex') ? scoped('pnl_management', { period: range, group_by: ['pnl_line'], metrics }) : null)
}

export function usePnlRelations(range: DateRange): ReportState {
  const { scoped, catalog } = useFinance()
  const metrics = pnlMetrics(catalog, ['revenue', 'opex'])
  return useReport('pnl_management', metrics.includes('opex') ? scoped('pnl_management', { period: range, group_by: ['relation'], metrics }) : null)
}

export function usePnlTrend(range: DateRange): ReportState {
  const { scoped, catalog } = useFinance()
  const metrics = pnlMetrics(catalog, ['revenue', 'opex', 'ebitda'])
  return useReport('pnl_management', metrics.includes('ebitda') ? scoped('pnl_management', { period: range, group_by: ['period'], metrics }) : null)
}

export function usePayroll(range: DateRange, dimension: 'department' | 'period'): ReportState {
  const { scoped, catalog } = useFinance()
  return useReport(
    'payroll_services',
    catalog.has('payroll_services')
      ? scoped('payroll_services', {
          period: range,
          group_by: [dimension],
          metrics: ['payroll_amount'],
          ...(dimension === 'department' ? { order_by: { key: 'payroll_amount', direction: 'desc' as const } } : {}),
        })
      : null,
  )
}

export function useCashBySection(range: DateRange): ReportState {
  const { scoped, catalog } = useFinance()
  return useReport(
    'cashflow',
    catalog.hasDimension('cashflow', 'cf_section')
      ? scoped('cashflow', { period: range, group_by: ['cf_section'], metrics: ['inflow', 'outflow', 'net_flow'] })
      : null,
  )
}

/** Выплаты ДДС по строкам P&L — для сверки с расходами по поступлениям. */
export function useCashByPnlLine(range: DateRange): ReportState {
  const { scoped, catalog } = useFinance()
  return useReport(
    'cashflow',
    catalog.hasDimension('cashflow', 'pnl_line') ? scoped('cashflow', { period: range, group_by: ['pnl_line'], metrics: ['outflow'] }) : null,
  )
}

export function useCustomers(range: DateRange): ReportState {
  const { scoped, catalog } = useFinance()
  // Как в useSalesSource: скрытый показатель в запросе — отказ 403 на весь
  // блок. Без маржи таблица ABC строится по одной выручке.
  const granted = new Set(catalog.descriptor('abc_customers')?.metrics.map((item) => item.key) ?? [])
  const metrics = ['revenue', 'gross_profit', 'margin_percent'].filter((key) => key === 'revenue' || granted.has(key))
  return useReport(
    'abc_customers',
    scoped('abc_customers', {
      period: range,
      group_by: ['counterparty'],
      metrics,
      order_by: { key: 'revenue', direction: 'desc' },
    }),
  )
}

export const OVERDUE_KEYS = ['overdue_1_30', 'overdue_31_60', 'overdue_61_90', 'overdue_90_plus'] as const

export const BUCKETS = [
  { key: 'not_overdue', label: 'Не просрочено', color: 'var(--age-0)' },
  { key: 'overdue_1_30', label: '1–30 дней', color: 'var(--age-1)' },
  { key: 'overdue_31_60', label: '31–60 дней', color: 'var(--age-2)' },
  { key: 'overdue_61_90', label: '61–90 дней', color: 'var(--age-3)' },
  { key: 'overdue_90_plus', label: 'свыше 90 дней', color: 'var(--age-4)' },
] as const

/** Покрытие движения денег: свои месяцы, не продаж — выгрузки идут разными потоками. */
export function useCashCoverage(): Coverage {
  const { catalog, coverage } = useFinance()
  const granted = catalog.descriptor('cashflow')?.metrics.some((metric) => metric.key === 'inflow') ?? false
  return useCoverage(granted ? 'cashflow' : null, 'inflow', coverage.notBefore)
}
